<template>
	<div
		class="tab-bar-wrapper items-center justify-center shrink h-[30px] inline-flex flex-row gap-2 bg-white max-w-full flex-nowrap overflow-hidden"
		style="app-region: drag;">
		<div class="tab-bar" ref="tab-bar"
		     :class="{ 'fade-left': ((tabBar?.scrollLeft > 0) && !dropdownEnabled) ?? false, 'fade-right': (isOverflowing && !dropdownEnabled) ?? false, 'overflow-x-hidden': dropdownEnabled }">
			<div
				@click="goToHome()"
				class="mr-2 w-fit inline-flex items-center cursor-pointer select-none rounded-t p-1.5 relative border-l border-t last:border-r shadow"
				:class="{
     'text-sm text-white bg-primary border-primary p-1': activeTab == null,
      'text-xs hover:text-primary': activeTab !== null
    }"
				style="transition: all 0.2s;will-change: font-size, color;"
			>
				<AppIcon icon="home"></AppIcon>
			</div>
			<AppContextMenu
				v-for="(tab, idx) in sessionTabs.slice(0, maximumVisibleTabs)"
				:key="tab.fullPath"
				class="inline-flex"
				style="app-region: no-drag;"
				:menu-items="[
        { text: 'Alle tabbladen sluiten', action: () => onCloseAllTabs() },
        { text: 'Rechter tabbladen sluiten', action: () => onCloseTabsToRight(idx) },
      ]"
			>
				<WindowTab
					id="windowTab"
					v-bind="{ ...tab, active: tab.fullPath === activeTab?.fullPath }"
					draggable="true"
					@dragover.prevent=""
					@dragstart="onDragStart(idx)"
					@dragend="onDragEnd"
					@drop="onDrop(idx)"
					@click="activateTab(tab)"
					@click.middle="closeTab(idx, true)"
					@close="closeTab(idx, true)"
				/>
			</AppContextMenu>
		</div>
		<template v-if="isOverflowing && dropdownEnabled">
			<VDropdown :distance="6" :triggers="defaultTooltipTriggers" :popperTriggers="defaultTooltipTriggers"
			           no-auto-focus>
				<template #default="{ shown }">
					<button
						class="inline-flex items-center cursor-pointer select-none rounded-t p-1.5 border-l border-t last:border-r shadow">
						<AppIcon
							icon="chevron-right"
							class="transition hover:rotate-90"
							:class="{ 'rotate-90': shown }"
						/>
					</button>
				</template>
				<template #popper>
					<div class="max-h-[60vh] overflow-y-auto scrollbar-hidden">
						<div v-for="(tab, idx) in sessionTabs.slice(maximumVisibleTabs)">
							<WindowTab
								v-bind="{ ...tab, active: false }"
								class="!w-[14rem] !text-base"
								draggable="true"
								@dragover.prevent=""
								@click="() => {
									bringTabForward(tab)
									activateTab(tab)
								}"
								@click.middle="closeTabInDropdown(idx, true)"
								@close="closeTabInDropdown(idx, true)"
							/>
						</div>
					</div>
				</template>
			</VDropdown>
		</template>
		<VDropdown
			class="notification-bell flex-initial flex ml-auto h-full self-center"
			:class="{ 'pr-[150px]': isDesktop(), 'pr-2': !isDesktop()  }"
			style="app-region: no-drag;"
			:distance="10"
			:skidding="-64"
			v-model:shown="showNotifications"
		>
			<AppIconLayers class="text-3xl h-[0.875em]">
				<AppIcon :icon="['fal', 'bell']"></AppIcon>
				<AppIconLayersText class="text-4xl" size="lg" counter
				                   :value="notifications.filter(n => n.readAt == null).length" position="top-right"/>
			</AppIconLayers>
			<template #popper>
				<div class="w-fit 2xl:w-[20vw] max-w-[400px] max-h-[400px] p-3 inline-flex flex-wrap overflow-y-auto">
					<div class="w-full flex gap-2">
						<AppButton type="button"
						           color="info"
						           class="font-medium"
						           icon="list-check"
						           :disabled="notifications.filter(n => n.readAt == null).length == 0"
						           @click="markAllNotifications">
							<span>Markeer als gelezen</span>
						</AppButton>
						<AppButton type="button"
						           class="font-medium ml-auto"
						           icon="list"
						           @click="openNotificationIndex">
							<span>Alle notificaties</span>
						</AppButton>
					</div>
					<div v-if="notifications.filter(n => n.readAt == null).length > 0" class="w-full">
						<AppAlert dismissable dismissable-icon="eye" :has-details="hasDetails(notification)"
						          has-details-icon="info" @dismiss="onDismissNotification(notification)"
						          @read-details="readDetailsNotification(notification)"
						          :type="notification.type.toLowerCase()"
						          v-for="notification in notifications.filter(n => n.readAt == null).slice(0, 6)">
							{{ utcDate(notification.createdAt).format("DD-MM-YYYY - HH:mm") }}<br>
							<b>{{ notification.title }}</b><br>
							<p v-if="notification.message" v-text="notification.message"></p>
							<p v-if="showDetails.includes(notification.id)">
								<div v-for="detail in notification.details">
									<p>{{ detail }}</p><br>
								</div>
							</p>
						</AppAlert>
					</div>
					<AppAlert type="info" inline class="select-none mt-1"
					          v-if="notifications.filter(n => n.readAt == null).length == 0">
						Er zijn geen ongelezen notificaties.
					</AppAlert>
				</div>
			</template>
		</VDropdown>
	</div>
</template>

<script setup lang="ts">
import { Notification, markNotificationAsRead } from "@q-food/q-food";
import { AppTab, useAppTabs } from "~/stores/useAppTabs";
import { useUserStore } from "~stores/User.ts";
import { utcDate } from "~utils/Date.ts";

const { push } = useRouter();

import { isDesktop } from "~utils/RuntimeEnvironment";
import { defaultTooltipTriggers } from "~utils/platformUtils.ts";

const { notifications } = storeToRefs(useUserStore());
const { sessionTabs, activateTab, removeTab, activeTab, goToHome } = useAppTabs();
const { activeUser } = storeToRefs(useUserStore());

const fadeLeft = ref(false);
const fadeRight = ref(false);
const draggingIndex = ref(null as number | null);
const showDetails = ref<string[]>([]);
const showNotifications = ref<boolean>(false);

const { currentRoute } = useRouter();
const tabBar = useTemplateRef("tab-bar");
import { useElementSize } from "@vueuse/core";

const { width } = useElementSize(tabBar);

const dropdownEnabled = activeUser.value?.settings?.tabDropdown ?? false;
let tabWidth: number | undefined = undefined;
const maximumVisibleTabs = ref(6);
const isOverflowing = ref(false);

onMounted(() => {
	if (dropdownEnabled) {
		overFlowCheck();
		watch(sessionTabs, () => {
			overFlowCheck();
		});

		watch(width, () => {
			overFlowCheck();
		});
	}
});

function onDragStart(index: number) {
	draggingIndex.value = index;
}

function openNotificationIndex() {
	showNotifications.value = false;
	push("/user-notification-log/index");
}

function overFlowCheck() {
	if (!tabWidth) {
		// don't put in onMounted, because you can load page without tabs
		tabWidth = document.getElementById("windowTab")?.clientWidth;
	}

	if (!tabBar.value || !tabWidth) {
		isOverflowing.value = false;
		return;
	}

	const tabBarWidth = tabBar.value?.clientWidth! - 37.5; // subtract 37.5 because of notification-bell
	maximumVisibleTabs.value = Math.floor(tabBarWidth / tabWidth);

	isOverflowing.value = sessionTabs.value.length > maximumVisibleTabs.value;
}

function bringTabForward(tab: AppTab) {
	sessionTabs.value = sessionTabs.value?.filter((existingTab) => existingTab != tab);
	sessionTabs.value = [tab, ...sessionTabs.value];
}

function onDragEnd() {
	draggingIndex.value = null;
}

const updateFades = () => {
	if (!tabBar.value) return;

	const el = tabBar.value;
	fadeLeft.value = el.scrollLeft > 0;
	fadeRight.value = el.scrollLeft + el.clientWidth < el.scrollWidth;
};

function onDrop(index: number) {
	if (draggingIndex.value == null) return;

	swapArrayElements(sessionTabs.value, draggingIndex.value, index);

	draggingIndex.value = null;
}

function closeTabInDropdown(tabIndex: number, activeTabCheck = false) {
	const newIndex = tabIndex + maximumVisibleTabs.value;
	closeTab(newIndex, activeTabCheck);
}

function closeTab(tabIndex: number, activeTabCheck = false) {
	const tabRoute = sessionTabs.value[tabIndex].fullPath;

	if (tabRoute in window.ontabclose) {
		const closeTabEvent = {
			prevent: false,
		};

		try {
			window.ontabclose[tabRoute](closeTabEvent);
		} catch (e) {
			closeTabEvent.prevent = false;
		}

		if (closeTabEvent.prevent) {
			return;
		}

		delete window.ontabclose[tabRoute];
	}

	const activeTab = activeTabCheck && currentRoute.value.fullPath === tabRoute;

	removeTab(tabIndex, activeTab);
}

function onCloseAllTabs() {
	sessionTabs.value.forEach((tab, index) => {
		closeTab(0);
	});
}

async function onCloseTabsToRight(fromIndex) {
	while (sessionTabs.value.length - 1 > fromIndex) {
		closeTab(fromIndex + 1);
	}
}

const swapArrayElements = (array: any[], index1: number, index2: number) => {
	// Checking parameters for errors
	if (index1 < 0 || index2 < 0 || index1 >= array.length || index2 >= array.length) {
		console.error("Invalid index passed to swapArrayElements()");

		return array;
	}

	// Swapping elements and returning the mutated array
	[array[index1], array[index2]] = [array[index2], array[index1]];

	return array;
};

async function markAllNotifications() {
	const userStore = useUserStore();
	await userStore.readAllNotifications();
}

async function onDismissNotification(notification: Notification) {
	notification.readAt = utcDate().format();
	await markNotificationAsRead(notification.id);
}

function readDetailsNotification(notification: Notification) {
	if (showDetails.value.includes(notification.id)) {
		showDetails.value = showDetails.value.filter((id) => id !== notification.id);
	} else {
		showDetails.value.push(notification.id);
	}
}

function hasDetails(notification: Notification) {
	return notification.details.length > 0;
}
</script>

<style scoped></style>
